import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Card from '@/components/card';
import Title from '@/components/title';
import {
  ICustomerOrderReportReq,
  ICustomerOrderReportRes,
} from '@/interfaces/report';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from 'antd';
import locale from 'antd/es/date-picker/locale/th_TH';
import { ReportMenu } from '@/configs/menuItems';
import { getPathSegment } from '@/utils/pathUtils';
import SubMenu from '@/components/menu/sub-menu';
import { AxiosResponse } from 'axios';
import { searchCustomerOrderReport } from '@/adapters/report';
import CustomerSelector from '@/components/selector/customer-selector';
import { ICustomer } from '@/interfaces/customer';
import ExcelExport from './components/export-report';
import CustomerOrderReportTable from './components/customer-order-report-table';
import CustomerOrderSetting from './components/customer-order-setting';
import BusinessUnitSelector from '@/components/selector/business-unit-selector';
import { useAuth } from '@/contexts/AuthContext';

const CustomerOrderReport = () => {
  const pathSegment = getPathSegment();
  const { userInfo } = useAuth();
  const [criteria, setCriteria] = useState<ICustomerOrderReportReq>({
    customerId: null,
    startDate: dayjs().startOf('month').toDate(),
    endDate: dayjs().toDate(),
    businessUnitId: userInfo?.bu || undefined,
  });

  const { data, isLoading } = useQuery<
    AxiosResponse<ICustomerOrderReportRes[]>,
    Error
  >({
    queryKey: ['search-customer-order-report', criteria],
    queryFn: async () => await searchCustomerOrderReport(criteria),
    // enabled: !!criteria.customerId,
  });

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      const startDate = dayjs(dates[0]).format('YYYY-MM-DD');
      const endDate = dayjs(dates[1]).format('YYYY-MM-DD');
      setCriteria({
        ...criteria,
        startDate: startDate,
        endDate: endDate,
      });
    } else {
      setCriteria({
        ...criteria,
        startDate: null,
        endDate: null,
      });
    }
  };

  return (
    <>
      <SubMenu active={pathSegment} items={ReportMenu} />
      <Card customClass="min-h-[200px]">
        <Title title="Customer Order Report">
          <div className="flex flex-row gap-2">
            <CustomerOrderSetting />
            <ExcelExport
              data={data?.data || []}
              startDate={dayjs(criteria?.startDate).format('YYYY')}
              endDate={dayjs(criteria?.endDate).format('YYYY')}
            />
          </div>
        </Title>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <div className="flex flex-col md:flex-row justify-between gap-3">
              <BusinessUnitSelector
                isAll
                handleSelectData={id => {
                  setCriteria({
                    ...criteria,
                    businessUnitId: id,
                  });
                }}
                value={criteria.businessUnitId || undefined}
                disabled={!!userInfo?.bu}
              />
              <DatePicker.RangePicker
                size="large"
                locale={locale}
                onChange={onRangeChange}
                defaultValue={[
                  dayjs(criteria?.startDate as string),
                  dayjs(criteria?.endDate as string),
                ]}
                width="300px"
                allowClear={false}
              />
              <CustomerSelector
                handleSelectData={function (
                  customer: ICustomer | undefined,
                ): void {
                  setCriteria({
                    ...criteria,
                    customerId: customer?.id || null,
                  });
                }}
                customClass="md:max-w-[400px] md:min-w-[400px]"
              />
            </div>
          </div>
          <CustomerOrderReportTable
            margin={0}
            report={data?.data || []}
            loading={isLoading}
            onTableChange={() => console.log()}
          />
        </div>
      </Card>
    </>
  );
};

export default CustomerOrderReport;
